import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShieldCheck, FileText, Download, Eye, EyeOff, ChevronDown, ChevronUp, Video, Image as ImageIcon } from "lucide-react";
import { NSBVerifiedInline } from "./NSBVerifiedBadge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function FileIcon({ contentType }) {
  if (contentType?.startsWith("video/")) return <Video size={16} className="text-purple-500" />;
  if (contentType?.startsWith("image/")) return <ImageIcon size={16} className="text-blue-500" />;
  return <FileText size={16} className="text-red-500" />;
}

export function ScoutingDataPanel({ profile, onVisibilityChange }) {
  const [files, setFiles] = useState([]);
  const [expanded, setExpanded] = useState({
    grades: true,
    arsenal: true,
    assessment: true,
    files: true,
  });

  const visibility = profile?.field_visibility || {};

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/athlete/my-files`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setFiles(data.files || []);
        }
      } catch {
        // silent
      }
    };
    fetchFiles();
  }, []);

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  const grades = profile?.nsb_grades || {};
  const pitchArsenal = profile?.pitch_arsenal || [];
  const notes = profile?.scouting_notes || {};
  const metrics = profile?.metrics || {};
  const hasData = profile?.has_scouting_report || Object.keys(grades).length > 0 || pitchArsenal.length > 0 || files.length > 0;

  if (!hasData) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 text-center" data-testid="scouting-data-empty">
        <ShieldCheck size={32} className="text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">No scouting data or files yet.</p>
        <p className="text-xs text-gray-400 mt-1">When your admin uploads reports, files, or evaluations, they will appear here.</p>
      </div>
    );
  }

  const VisToggle = ({ field }) => {
    const val = visibility[field] || "public";
    const next = val === "public" ? "coaches" : val === "coaches" ? "private" : "public";
    const labels = { public: "Visible to All", coaches: "Coaches Only", private: "Hidden" };
    const colors = { public: "text-green-600 bg-green-50", coaches: "text-blue-600 bg-blue-50", private: "text-gray-500 bg-gray-100" };
    const icons = { public: Eye, coaches: ShieldCheck, private: EyeOff };
    const Icon = icons[val];
    return (
      <button
        onClick={() => onVisibilityChange?.(field, next)}
        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${colors[val]} transition hover:opacity-80`}
        title={`Click to change: ${labels[next]}`}
        data-testid={`vis-toggle-${field}`}
      >
        <Icon size={11} />
        {labels[val]}
      </button>
    );
  };

  const SectionHeader = ({ title, section, visField }) => (
    <button onClick={() => toggle(section)} className="flex items-center justify-between w-full mb-3">
      <div className="flex items-center gap-2">
        <h4 className="font-semibold text-gray-900">{title}</h4>
        {visField && <VisToggle field={visField} />}
      </div>
      {expanded[section] ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="scouting-data-panel">
      {/* Key Verified Metrics */}
      {Object.keys(metrics).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <SectionHeader title="Verified Performance Metrics" section="grades" visField="metrics" />
          {expanded.grades && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {metrics.fastball_velocity && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center relative">
                    <div className="absolute top-1.5 right-1.5"><NSBVerifiedInline verified={true} /></div>
                    <div className="text-xs text-gray-500">Max Velo</div>
                    <div className="text-xl font-bold text-gray-900">{metrics.fastball_velocity}</div>
                    <div className="text-xs text-gray-400">mph</div>
                  </div>
                )}
                {metrics.avg_fastball_velocity && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center relative">
                    <div className="absolute top-1.5 right-1.5"><NSBVerifiedInline verified={true} /></div>
                    <div className="text-xs text-gray-500">Avg FB Velo</div>
                    <div className="text-xl font-bold text-gray-900">{metrics.avg_fastball_velocity}</div>
                    <div className="text-xs text-gray-400">mph</div>
                  </div>
                )}
                {metrics.spin_rate && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center relative">
                    <div className="absolute top-1.5 right-1.5"><NSBVerifiedInline verified={true} /></div>
                    <div className="text-xs text-gray-500">Max Spin</div>
                    <div className="text-xl font-bold text-gray-900">{metrics.spin_rate}</div>
                    <div className="text-xs text-gray-400">rpm</div>
                  </div>
                )}
                {metrics.extension && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center relative">
                    <div className="absolute top-1.5 right-1.5"><NSBVerifiedInline verified={true} /></div>
                    <div className="text-xs text-gray-500">Extension</div>
                    <div className="text-xl font-bold text-gray-900">{metrics.extension}</div>
                    <div className="text-xs text-gray-400">ft</div>
                  </div>
                )}
                {metrics.exit_velocity && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center relative">
                    <div className="absolute top-1.5 right-1.5"><NSBVerifiedInline verified={true} /></div>
                    <div className="text-xs text-gray-500">Exit Velo</div>
                    <div className="text-xl font-bold text-gray-900">{metrics.exit_velocity}</div>
                    <div className="text-xs text-gray-400">mph</div>
                  </div>
                )}
                {metrics.sixty_time && (
                  <div className="bg-gray-50 rounded-lg p-3 text-center relative">
                    <div className="absolute top-1.5 right-1.5"><NSBVerifiedInline verified={true} /></div>
                    <div className="text-xs text-gray-500">60-Yard</div>
                    <div className="text-xl font-bold text-gray-900">{metrics.sixty_time}</div>
                    <div className="text-xs text-gray-400">sec</div>
                  </div>
                )}
              </div>

              {/* Tool Grades */}
              {Object.keys(grades).length > 0 && (
                <div>
                  <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">NSB Tool Grades (20-80 Scale)</h5>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {Object.entries(grades).map(([tool, grade]) => (
                      <div key={tool} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="text-xs text-gray-600 capitalize">{tool.replace(/_/g, " ")}</span>
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full"><NSBVerifiedInline verified={true} /></span>
                          <span className={`text-sm font-bold ${
                            grade >= 60 ? "text-green-600" : grade >= 50 ? "text-blue-600" : grade >= 40 ? "text-amber-600" : "text-gray-500"
                          }`}>{grade}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Pitch Arsenal */}
      {pitchArsenal.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <SectionHeader title="Pitch Arsenal" section="arsenal" visField="scouting_reports" />
          {expanded.arsenal && (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm min-w-[480px]">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="pb-2 pl-2 font-medium">Pitch</th>
                    <th className="pb-2 font-medium">Usage</th>
                    <th className="pb-2 font-medium">Avg Velo</th>
                    <th className="pb-2 font-medium">Max Velo</th>
                    <th className="pb-2 font-medium">Avg Spin</th>
                    <th className="pb-2 font-medium">H Mov</th>
                    <th className="pb-2 font-medium">V Mov</th>
                  </tr>
                </thead>
                <tbody>
                  {pitchArsenal.map((p, i) => (
                    <tr key={i} className="border-b border-gray-50">
                      <td className="py-2 pl-2 font-medium text-gray-900">{p.pitch || p.pitch_type}</td>
                      <td className="py-2 text-gray-600">{p.usage_pct || p.usage}%</td>
                      <td className="py-2 text-gray-600">{p.avg_velo}</td>
                      <td className="py-2 text-gray-600">{p.max_velo}</td>
                      <td className="py-2 text-gray-600">{p.avg_spin}</td>
                      <td className="py-2 text-gray-600">{p.h_mov || "--"}</td>
                      <td className="py-2 text-gray-600">{p.v_mov || "--"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Scout Assessment */}
      {(notes.summary || notes.strengths || notes.development_areas || notes.projection) && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <SectionHeader title="Scout Assessment" section="assessment" visField="scouting_reports" />
          {expanded.assessment && (
            <div className="space-y-4 text-sm">
              {notes.summary && (
                <div>
                  <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Executive Summary</h5>
                  <p className="text-gray-700 leading-relaxed bg-gray-50 p-3 rounded-lg">{notes.summary}</p>
                </div>
              )}
              {notes.strengths && (
                <div>
                  <h5 className="text-xs font-medium text-green-600 uppercase tracking-wider mb-1">Strengths</h5>
                  <p className="text-gray-700 leading-relaxed">{typeof notes.strengths === "string" ? notes.strengths : JSON.stringify(notes.strengths)}</p>
                </div>
              )}
              {notes.development_areas && (
                <div>
                  <h5 className="text-xs font-medium text-amber-600 uppercase tracking-wider mb-1">Areas for Development</h5>
                  <p className="text-gray-700 leading-relaxed">{typeof notes.development_areas === "string" ? notes.development_areas : JSON.stringify(notes.development_areas)}</p>
                </div>
              )}
              {notes.projection && (
                <div>
                  <h5 className="text-xs font-medium text-blue-600 uppercase tracking-wider mb-1">Projection</h5>
                  <p className="text-gray-700 leading-relaxed">{notes.projection}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Uploaded Files & Documents */}
      {files.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <SectionHeader title={`Reports & Files (${files.length})`} section="files" visField="files" />
          {expanded.files && (
            <div className="space-y-2">
              {files.map((f) => (
                <div key={f.file_id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100 hover:border-gray-200 transition">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileIcon contentType={f.content_type} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{f.filename || "Uploaded File"}</p>
                      <p className="text-xs text-gray-400">{f.content_type} {f.size_bytes ? `· ${(f.size_bytes / 1024).toFixed(0)} KB` : ""}</p>
                    </div>
                    <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
                      <ShieldCheck size={10} /> Admin
                    </span>
                  </div>
                  <a
                    href={`${API_URL}/api/player-files/${f.file_id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-200 rounded-lg transition flex-shrink-0"
                    title="Download"
                    data-testid={`download-file-${f.file_id}`}
                  >
                    <Download size={16} className="text-gray-500" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
